import { capitalizeFirst } from "../../../../hooks/utilsFn/utilsFn";
import { useSelector } from "../../../../imports/generalImport";
import {
  Box,
  Typography,
  List,
  Divider,
  ListItem,
  Skeleton
} from "../../../../imports/muiImport";
import { handleAmount } from "../../../../shared/extra";
import { selectOrderSummary } from "../../../../store/denominations/selectors";

function OrderSummaryItem({
    label,
    value,
    highlight = false,
    labelWidth = "65%",
    valueWidth = "35%",
    color = "#000000",
    fontWeight = 600,
    isLoading = false,
}) {
    return (
        <ListItem>
            <Typography
                sx={{
                    minWidth: labelWidth,
                    fontSize: highlight ? { xs: "16px", lg: "18.5px" } : { xs: "13px", lg: "16px" },
                    fontWeight: highlight ? fontWeight : 500,
                    color: color,
                    fontFamily: "Be Vietnam Pro",
                    textAlign: "left",
                }}
            >
                {isLoading ? <Skeleton width={100} /> : capitalizeFirst(label)}
            </Typography>

            <Typography
                sx={{
                    minWidth: valueWidth,
                    fontSize: highlight ? { xs: "13.5px", sm: "16.5px" } : { xs: '13px', lg: '16px' },
                    fontWeight: 500,
                    textAlign: "right",
                    color: color,
                    fontFamily: "Be Vietnam Pro"
                }}
            >
                {isLoading ? <Skeleton width={80} /> : value}
            </Typography>
        </ListItem>
    );
}

function PaymentOrderReceipt({ walletPayment }) {
    // Use centralized selectors - calculations are memoized and cached
    const { totalValue, totalDiscount, netPayable, totalCashback } = useSelector(selectOrderSummary);
    const PRODUCTDETAILS = useSelector((state) => state?.ProductDetail?.allData);
    const userBalanceStore = useSelector((state) => state?.UserProfile?.allData);

    // Loading states - check if data is still loading
    const isOrderSummaryLoading = useSelector((state) => state?.denominations?.loading);
    const isProductDetailsLoading = useSelector((state) => state?.ProductDetail?.loading);
    const isUserProfileLoading = useSelector((state) => state?.UserProfile?.loading);

    // Edge case: Check if we have valid data
    const hasValidData =
        totalValue !== undefined &&
        totalValue !== null &&
        netPayable !== undefined &&
        netPayable !== null &&
        PRODUCTDETAILS?.denominations?.length > 0;

    // Determine if we should show skeleton
    const isLoading =
        isOrderSummaryLoading ||
        isProductDetailsLoading ||
        isUserProfileLoading ||
        !hasValidData;

    const discountName = PRODUCTDETAILS?.denominations?.[0]?.cashbackOrDiscount || "Discount";
    const discountValue =
        discountName?.toUpperCase() === "CASHBACK"
            ? totalCashback || 0
            : totalDiscount || 0;

    const walletAmount = Number(userBalanceStore?.walletDatum?.amount || 0);

    const calculateNetPay = () => {
        try {
            let value = 0;

            if (walletPayment) {
                if (walletAmount > 0) {
                    if (discountName?.toUpperCase() === "CASHBACK") {
                        value = netPayable - walletAmount;
                    } else {
                        value = netPayable - walletAmount;
                    }

                    if (value < 0) {
                        value = 0;
                    }
                }
            } else {
                value = netPayable;
            }

            return handleAmount(value) || "0.00";
        } catch (error) {
            console.error("Error calculating net pay:", error);
            return "0.00";
        }
    };

    const calculateWalletPayment = () => {
        try {
            let value = 0;

            if (walletPayment) {
                if (netPayable > walletAmount) {
                    value = walletAmount;
                } else {
                    value = walletAmount - (walletAmount - netPayable);
                }
            }
            return handleAmount(value) || "0.00";
        } catch (error) {
            console.error("Error calculating wallet payment:", error);
            return "0.00";
        }
    };

    // Fallback values for edge cases
    const safeTotalValue = totalValue || 0;
    const safeNetPayable = netPayable || 0;

    const summaryData = [
        {
            label: "Total Value",
            value: `AED ${handleAmount(safeTotalValue) || "0.00"}`,
        },
        {
            label: `${discountName}`,
            value: `AED ${handleAmount(discountValue) || "0.00"}`,
            color: "#007411",
        },
        ...(walletPayment
            ? [
                {
                    label: "Wallet",
                    value: `AED ${calculateWalletPayment()}`,
                    color: "#007411",
                },
            ]
            : []),
        {
            label: "You Pay",
            value: `AED ${calculateNetPay()}`,
            highlight: true,
        },
    ];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Divider
                sx={{
                    border: '0.7px solid #E4C18F',
                    mb: 2
                }}
            />
            <Typography
                variant="body1"
                gutterBottom
                sx={{
                    color: "#002865",
                    fontSize: { xs: "16px", md: "18px", lg: "20px" },
                    fontWeight: 500,
                    textAlign: "left",
                    mb: { xs: 1, md: 2 },
                    fontFamily: "Onest"
                }}
            >
                Order summary
            </Typography>

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start'
                }}
            >
                <List>
                    {summaryData.map((item, index) => (
                        <Box key={index}>
                            {item.highlight && (
                                <Divider
                                    sx={{
                                        border: '0.7px solid #E4C18F',
                                        my: 1,
                                    }}
                                />
                            )}
                            <OrderSummaryItem
                                label={item.label}
                                value={item.value}
                                highlight={item.highlight}
                                color={item.color}
                                isLoading={isLoading}
                            />
                        </Box>
                    ))}
                </List>
            </Box>
        </Box>
    );
}

export default PaymentOrderReceipt;